'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  image: string;
  alt: string;
}

interface Order {
  id: string;
  orderNumber: string;
  date: string;
  status: 'delivered' | 'processing' | 'cancelled';
  items: OrderItem[];
  totalAmount: number;
  deliveryAddress: string;
}

interface OrderHistorySectionProps {
  orders: Order[];
}

const OrderHistorySection = ({ orders }: OrderHistorySectionProps) => {
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'delivered' | 'processing' | 'cancelled'>('all');

  const filteredOrders = selectedFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === selectedFilter);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-success/10 text-success';
      case 'processing':
        return 'bg-warning/10 text-warning';
      case 'cancelled':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'CheckCircleIcon';
      case 'processing':
        return 'ClockIcon';
      case 'cancelled':
        return 'XCircleIcon';
      default:
        return 'QuestionMarkCircleIcon';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
            <Icon name="ClipboardDocumentListIcon" size={24} className="text-accent" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground">Order History</h2>
            <p className="text-sm text-muted-foreground">{orders.length} total orders</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {['all', 'delivered', 'processing', 'cancelled'].map((filter) => (
          <button
            key={filter}
            onClick={() => setSelectedFilter(filter as any)}
            className={`px-4 py-2 rounded-md font-medium text-sm transition-smooth whitespace-nowrap ${
              selectedFilter === filter
                ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                : 'bg-muted text-foreground hover:bg-border'
            }`}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredOrders.map((order) => (
          <div key={order.id} className="p-4 rounded-lg border border-border bg-background">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-medium text-sm text-foreground">Order #{order.orderNumber}</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStatusColor(order.status)}`}>
                    <Icon name={getStatusIcon(order.status) as any} size={14} className="inline mr-1" />
                    {order.status}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-1">Placed on {order.date}</p>
                <p className="text-xs text-muted-foreground">{order.deliveryAddress}</p>
              </div>
              <div className="text-right">
                <p className="font-data font-semibold text-lg text-primary">₹{order.totalAmount.toLocaleString('en-IN')}</p>
                <p className="text-xs text-muted-foreground">{order.items.length} items</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4 overflow-x-auto pb-2">
              {order.items.map((item) => (
                <div key={item.id} className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border border-border">
                  <AppImage
                    src={item.image}
                    alt={item.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-border">
              <Link
                href={`/order-tracking?orderId=${order.id}`}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
              >
                <Icon name="EyeIcon" size={16} />
                <span>View Details</span>
              </Link>
              {order.status === 'delivered' && (
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-muted text-foreground rounded-md font-medium text-sm transition-smooth hover:bg-border">
                  <Icon name="ArrowPathIcon" size={16} />
                  <span>Reorder</span>
                </button>
              )}
            </div>
          </div>
        ))}

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <Icon name="ClipboardDocumentListIcon" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground mb-4">No orders found</p>
            <Link
              href="/product-listing"
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
            >
              <Icon name="ShoppingBagIcon" size={16} />
              <span>Start Shopping</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistorySection;